import React, { useState } from 'react';
import SEOHead from '../components/SEOHead';
import StrategyCallModal from '../components/StrategyCallModal';
import { EnterpriseLead, toLeadPayload } from '../models/EnterpriseLead';
import { validateForm, ValidationRules } from '../utils/formValidation';
import { identifyVisitor } from '../utils/tracker';
import { getUTMParams } from '../services/utmService';
import api from '../utils/api';
import { Button } from '../colaberry/components/core/Button';
import { Badge } from '../colaberry/components/core/Badge';
import { Card } from '../colaberry/components/core/Card';
import { Input } from '../colaberry/components/core/Input';
import { Textarea } from '../colaberry/components/core/Textarea';
import { Checkbox } from '../colaberry/components/core/Checkbox';
import { shouldAskPhoneConsent } from '../components/PhoneConsentCheckbox';
import { PHONE_CONSENT_TEXT } from '../config/consentText';

const TRY_PATH = '/try';

const COMPANY_SIZES = [
  '1-49 employees',
  '50-249 employees',
  '250-999 employees',
  '1,000-4,999 employees',
  '5,000+ employees',
] as const;

const INDUSTRIES = [
  'Technology',
  'Finance & Banking',
  'Healthcare & Life Sciences',
  'Manufacturing',
  'Energy & Utilities',
  'Retail & eCommerce',
  'Government & Public Sector',
  'Logistics & Supply Chain',
  'Other',
] as const;

const CONTACT_RULES: ValidationRules = {
  required: ['fullName', 'email'],
  email: ['email'],
  phone: ['phone'],
};

const FORM_TYPE = 'enterprise_inquiry';

interface ContactForm {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  title: string;
  companySize: string;
  industry: string;
  message: string;
  consentContact: boolean;
  /** Optional, and separate from `consentContact` — see EnterpriseLead. */
  phoneConsent: boolean;
}

const EMPTY_FORM: ContactForm = {
  fullName: '',
  email: '',
  phone: '',
  company: '',
  title: '',
  companySize: '',
  industry: '',
  message: '',
  consentContact: false,
  phoneConsent: false,
};

function ContactPage() {
  const [showBooking, setShowBooking] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState('');
  const [form, setForm] = useState<ContactForm>(EMPTY_FORM);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError('');

    const validationErrors = validateForm(
      form as unknown as Record<string, unknown>,
      CONTACT_RULES,
    );
    if (!form.consentContact) {
      validationErrors.consentContact = 'You must agree to be contacted';
    }
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    try {
      const lead: EnterpriseLead = {
        fullName: form.fullName,
        email: form.email,
        phone: form.phone || undefined,
        company: form.company || undefined,
        title: form.title || undefined,
        companySize: form.companySize || undefined,
        industry: form.industry || undefined,
        message: form.message.trim() || undefined,
        consentContact: form.consentContact,
        // Only meaningful alongside a number, and the tick is only shown with one.
        phoneConsent: form.phoneConsent,
        formType: FORM_TYPE,
        ...getUTMParams(),
        pageOrigin: window.location.href,
      };
      await api.post('/api/leads', toLeadPayload(lead));
      // Backfills this person's earlier anonymous sessions onto their lead.
      identifyVisitor(lead.email, {
        name: lead.fullName,
        company: lead.company,
        phone: lead.phone,
        metadata: { source_form: FORM_TYPE, industry: lead.industry, title: lead.title },
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      if (err.response?.status === 400 && err.response?.data?.details) {
        const fieldErrors: Record<string, string> = {};
        err.response.data.details.forEach((d: { field: string; message: string }) => {
          fieldErrors[d.field] = d.message;
        });
        setErrors(fieldErrors);
      } else {
        setServerError('Something went wrong. Please try again later.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Contact"
        description="Prefer to talk first? Reach the Colaberry team or book a walkthrough. The easiest way to see the platform is to start free and explore the whole thing yourself, no credit card."
      />

      {/* Hero */}
      <section
        aria-label="Page Header"
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: 'var(--surface-inverse)',
          color: 'var(--text-on-accent)',
          padding: 'var(--space-20) 0 var(--space-16)',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            backgroundImage:
              "linear-gradient(180deg, color-mix(in srgb, var(--surface-inverse) 72%, transparent), color-mix(in srgb, var(--surface-inverse) 92%, transparent)), url('/img/team-collab.jpg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div
          className="container text-center"
          style={{ position: 'relative', zIndex: 1, maxWidth: 820 }}
        >
          <Badge tone="red" dot style={{ marginBottom: 'var(--space-5)' }}>
            Start free anytime, no credit card
          </Badge>
          <h1
            className="cb-balance"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--fs-display)',
              fontWeight: 900,
              lineHeight: 1.05,
              margin: '0 0 var(--space-4)',
              color: 'var(--text-on-accent)',
            }}
          >
            Prefer to talk it through first?
          </h1>
          <p
            style={{
              fontSize: 'var(--fs-body-lg)',
              lineHeight: 1.6,
              opacity: 0.88,
              maxWidth: 620,
              margin: '0 auto var(--space-6)',
            }}
          >
            The easiest way to see the platform is to start free and explore the whole
            thing yourself, as both the learner and the admin. No credit card. If you would
            rather talk first, send a note below and we&rsquo;ll get back within one business day.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button as="a" href={TRY_PATH} size="lg" data-track="contact_hero_start_free">
              Start free
            </Button>
          </div>
        </div>
      </section>

      {/* Form section */}
      <section
        aria-label="Contact"
        style={{ background: 'var(--surface-sunken)', padding: 'var(--space-16) 0' }}
      >
        <div className="container" style={{ maxWidth: 860 }}>
          {submitted ? (
            <Card elevation="md" padded accent="green" role="alert">
              <div className="text-center" style={{ padding: 'var(--space-6) 0' }}>
                <Badge tone="green" dot style={{ marginBottom: 'var(--space-4)' }}>
                  Message received
                </Badge>
                <h2
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--fs-h2)',
                    color: 'var(--text-strong)',
                    margin: '0 0 var(--space-3)',
                  }}
                >
                  Thanks, we&rsquo;ll be in touch.
                </h2>
                <p
                  style={{
                    fontSize: 'var(--fs-body-lg)',
                    color: 'var(--text-body)',
                    maxWidth: 560,
                    margin: '0 auto var(--space-5)',
                  }}
                >
                  Our team will follow up shortly. In the meantime, you can start free and
                  explore the whole platform yourself, no credit card.
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-5)' }}>
                  <Button as="a" href={TRY_PATH} size="lg" data-track="contact_success_start_free">
                    Start free
                  </Button>
                </div>
                <p style={{ color: 'var(--text-muted)', margin: 0 }}>
                  Time-sensitive? Reach us at{' '}
                  <a href="mailto:info@colaberry.com" style={{ color: 'var(--brand-accent)' }}>
                    info@colaberry.com
                  </a>
                  .
                </p>
              </div>
            </Card>
          ) : (
            <Card elevation="md" padded>
              <p
                style={{
                  fontSize: 'var(--fs-body)',
                  color: 'var(--text-body)',
                  lineHeight: 1.6,
                  margin: '0 0 var(--space-5)',
                }}
              >
                This is the optional path. Start free whenever you like, no credit card. Or use
                this form to reach us with a question or to book a walkthrough, and we&rsquo;ll
                respond within one business day.
              </p>

              {serverError && (
                <div
                  role="alert"
                  style={{
                    background: 'var(--surface-card)',
                    border: '1px solid var(--status-danger)',
                    color: 'var(--status-danger)',
                    borderRadius: 'var(--radius-md)',
                    padding: 'var(--space-3) var(--space-4)',
                    margin: 'var(--space-4) 0',
                    fontSize: 'var(--fs-body-sm)',
                  }}
                >
                  {serverError}
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                <FieldGroup label="Your details">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <Input
                        label="Full name"
                        required
                        name="fullName"
                        value={form.fullName}
                        onChange={handleChange}
                        error={errors.fullName}
                        autoComplete="name"
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label="Work email"
                        required
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        error={errors.email}
                        autoComplete="email"
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label="Phone"
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        error={errors.phone}
                        placeholder="+1 (555) 123-4567"
                        autoComplete="tel"
                      />
                      {/*
                        Beside the field it belongs to, not down with the required
                        consent box, so it is unmistakably about this number.
                        Rendered with this page's own Checkbox primitive rather than
                        the shared Bootstrap one — the shared rule is
                        `shouldAskPhoneConsent` and the shared wording is
                        PHONE_CONSENT_TEXT; the markup belongs to the page.
                      */}
                      {shouldAskPhoneConsent(form.phone) && (
                        <div style={{ marginTop: 'var(--space-2)' }}>
                          <Checkbox
                            name="phoneConsent"
                            checked={form.phoneConsent}
                            onChange={handleChange}
                            label={
                              <span style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)' }}>
                                {PHONE_CONSENT_TEXT}
                              </span>
                            }
                          />
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <Input
                        label="Title (optional)"
                        name="title"
                        value={form.title}
                        onChange={handleChange}
                        error={errors.title}
                        autoComplete="organization-title"
                      />
                    </div>
                  </div>
                </FieldGroup>

                <FieldGroup label="Company (optional)">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <Input
                        label="Company"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        error={errors.company}
                        autoComplete="organization"
                      />
                    </div>
                    <div className="col-md-6">
                      <SelectField
                        label="Company size"
                        name="companySize"
                        value={form.companySize}
                        onChange={handleChange}
                        error={errors.companySize}
                        options={COMPANY_SIZES}
                      />
                    </div>
                    <div className="col-md-6">
                      <SelectField
                        label="Industry"
                        name="industry"
                        value={form.industry}
                        onChange={handleChange}
                        options={INDUSTRIES}
                      />
                    </div>
                  </div>
                </FieldGroup>

                <FieldGroup label="Anything to add?">
                  <Textarea
                    label="Message"
                    name="message"
                    rows={3}
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us what you want to build, or what you'd like to cover on a walkthrough."
                  />
                </FieldGroup>

                <div style={{ margin: 'var(--space-5) 0 var(--space-6)' }}>
                  <Checkbox
                    name="consentContact"
                    checked={form.consentContact}
                    onChange={handleChange}
                    label={
                      <span style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--text-body)' }}>
                        I agree to be contacted by Colaberry.{' '}
                        <span style={{ color: 'var(--status-danger)' }}>*</span>
                      </span>
                    }
                  />
                  {errors.consentContact && (
                    <div
                      style={{
                        color: 'var(--status-danger)',
                        fontSize: 'var(--fs-body-sm)',
                        marginTop: 'var(--space-1)',
                      }}
                    >
                      {errors.consentContact}
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={submitting}
                >
                  {submitting ? 'Sending…' : 'Send message'}
                </Button>
              </form>

              <p
                className="text-center"
                style={{
                  color: 'var(--text-muted)',
                  fontSize: 'var(--fs-body-sm)',
                  margin: 'var(--space-5) 0 0',
                  paddingTop: 'var(--space-4)',
                  borderTop: '1px solid var(--border-subtle)',
                }}
              >
                We never sell your information. Your data is used solely to respond to your request.
              </p>
            </Card>
          )}
        </div>
      </section>

      {/* Strategy Call CTA */}
      <section
        aria-label="Schedule Strategy Call"
        className="text-center"
        style={{
          background: 'var(--surface-inverse)',
          color: 'var(--text-on-accent)',
          padding: 'var(--space-20) 0',
        }}
      >
        <div className="container" style={{ maxWidth: 760 }}>
          <h2
            className="cb-balance"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--fs-h1)',
              fontWeight: 900,
              color: 'var(--text-on-accent)',
              margin: '0 0 var(--space-3)',
            }}
          >
            Learn With Claude. Build Through Colaberry. Deploy In The Real World.
          </h2>
          <p
            style={{
              fontSize: 'var(--fs-body-lg)',
              opacity: 0.85,
              margin: '0 auto var(--space-6)',
              maxWidth: 580,
            }}
          >
            Want a guided tour before you start free? Book a 30-minute walkthrough and we&rsquo;ll
            show you the learner experience and your management dashboard, side by side.
          </p>
          <Button variant="solid" tone="red" size="lg" onClick={() => setShowBooking(true)}>
            Schedule an Executive AI Strategy Call
          </Button>
          <div
            className="d-flex justify-content-center gap-4 flex-wrap"
            style={{ opacity: 0.7, marginTop: 'var(--space-6)' }}
          >
            <span style={{ fontSize: 'var(--fs-body-sm)' }}>30-minute focused session</span>
            <span style={{ fontSize: 'var(--fs-body-sm)' }}>No obligation</span>
            <span style={{ fontSize: 'var(--fs-body-sm)' }}>Start free whenever you like</span>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section
        aria-label="Contact Information"
        style={{ background: 'var(--surface-page)', padding: 'var(--space-16) 0' }}
      >
        <div className="container">
          <div className="row g-4">
            <div className="col-md-4">
              <Card elevation="sm" padded hoverable style={{ height: '100%' }}>
                <h3 style={infoTitle}>Email</h3>
                <p style={infoBody}>
                  <a href="mailto:info@colaberry.com" style={{ color: 'var(--brand-accent)' }}>
                    info@colaberry.com
                  </a>
                </p>
              </Card>
            </div>
            <div className="col-md-4">
              <Card elevation="sm" padded hoverable style={{ height: '100%' }}>
                <h3 style={infoTitle}>Social</h3>
                <p style={infoBody}>
                  <a
                    href="https://www.linkedin.com/company/colaberry"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--brand-accent)' }}
                  >
                    LinkedIn
                  </a>
                  {' · '}
                  <a
                    href="https://twitter.com/Colaberry"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--brand-accent)' }}
                  >
                    Twitter
                  </a>
                </p>
              </Card>
            </div>
            <div className="col-md-4">
              <Card elevation="sm" padded hoverable style={{ height: '100%' }}>
                <h3 style={infoTitle}>Walkthrough</h3>
                <p style={{ ...infoBody, marginBottom: 'var(--space-3)' }}>
                  Book a 30-minute call with our team.
                </p>
                <Button variant="outline" size="sm" onClick={() => setShowBooking(true)}>
                  Book a Call
                </Button>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <StrategyCallModal show={showBooking} onClose={() => setShowBooking(false)} pageOrigin="/contact" />
    </>
  );
}

/* ---------- Local presentational helpers ---------- */

const infoTitle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: 'var(--fs-h3)',
  color: 'var(--text-strong)',
  margin: '0 0 var(--space-2)',
};

const infoBody: React.CSSProperties = {
  color: 'var(--text-muted)',
  margin: 0,
};

interface FieldGroupProps {
  label: string;
  children: React.ReactNode;
}

function FieldGroup({ label, children }: FieldGroupProps) {
  return (
    <div style={{ marginBottom: 'var(--space-5)' }}>
      <p
        style={{
          fontSize: 'var(--fs-caption)',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          margin: '0 0 var(--space-3)',
        }}
      >
        {label}
      </p>
      {children}
    </div>
  );
}

interface SelectFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: readonly string[];
  required?: boolean;
  error?: string;
}

function SelectField({ label, name, value, onChange, options, required, error }: SelectFieldProps) {
  const id = `cf-${name}`;
  return (
    <div className="cb-min0">
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontSize: 'var(--fs-body-sm)',
          fontWeight: 500,
          color: 'var(--text-strong)',
          marginBottom: 'var(--space-1)',
        }}
      >
        {label}
        {required && <span style={{ color: 'var(--status-danger)' }}> *</span>}
      </label>
      <select
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        style={{
          width: '100%',
          minHeight: 48,
          padding: '0 var(--space-3)',
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--fs-body-sm)',
          color: 'var(--text-strong)',
          background: 'var(--surface-card)',
          border: `1px solid ${error ? 'var(--status-danger)' : 'var(--border-default)'}`,
          borderRadius: 'var(--radius-md)',
        }}
      >
        <option value="">Select…</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
      {error && (
        <div
          style={{
            color: 'var(--status-danger)',
            fontSize: 'var(--fs-body-sm)',
            marginTop: 'var(--space-1)',
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

export default ContactPage;
